// Characters domain query + mutation hooks.
//
// Mirrors useStories.ts, with one extra: generation is ASYNC, so useCharacters
// auto-polls (every 2s) while any row is still `generating`, then stops — the
// gallery flips to the rendered image without a manual refresh.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { Character, CreateCharacterInput, KreaLoraCatalog, Style, UpdateCharacterInput } from '@/api/types'

/** Stable cache key for the art-style catalog. */
export const stylesKey = ['styles'] as const

/** Stable cache key for the embedded Krea2 LoRA catalog. */
export const kreaLorasKey = ['krea-loras'] as const

/** Stable cache key for the recent-characters list. */
export const charactersKey = ['characters'] as const

/** useStyles — fetch the화풍 catalog (rarely changes; cached 5min). */
export function useStyles() {
  return useQuery<Style[]>({
    queryKey: stylesKey,
    queryFn: api.listStyles,
    staleTime: 5 * 60 * 1000,
  })
}

/** useKreaLoras — fetch the wrapped fal Krea 2 Style LoRA index. */
export function useKreaLoras() {
  return useQuery<KreaLoraCatalog>({
    queryKey: kreaLorasKey,
    queryFn: api.listKreaLoras,
    staleTime: 30 * 60 * 1000,
  })
}

/**
 * useCharacters — fetch the recent characters in stable creation order. Polls
 * every 2s while any character is still generating, then idles.
 */
export function useCharacters() {
  return useQuery<Character[]>({
    queryKey: charactersKey,
    queryFn: api.listCharacters,
    refetchInterval: (query) =>
      query.state.data?.some((c) => c.status === 'generating') ? 2000 : false,
  })
}

/**
 * useCreateCharacter — POST a style + subject. The returned `generating` row is
 * seeded into the list cache and the list is invalidated so the new card (and
 * its polling) appears immediately.
 */
export function useCreateCharacter() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateCharacterInput) => api.createCharacter(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: charactersKey })
    },
  })
}

/** useDeleteCharacter — remove a generated character row from the gallery. */
export function useDeleteCharacter() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.deleteCharacter(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: charactersKey })
    },
  })
}

/** useUpdateCharacter — rename a generated character for story matching. */
export function useUpdateCharacter() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateCharacterInput }) => api.updateCharacter(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: charactersKey })
    },
  })
}

/** useRetryCharacter — re-run generation on the same failed character row. */
export function useRetryCharacter() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.retryCharacter(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: charactersKey })
    },
  })
}
